import { useCallback, useEffect, useState } from "react";
import api, { errMsg, fmtMoney } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Plus, Trophy, Trash2, Pencil, Scale, Paperclip, Mail, History } from "lucide-react";
import { EvidencePanel } from "./EvidencePanel";
import { RfqDialog } from "./RfqDialog";

const emptyQuote = (req) => ({ id: null, supplier_id: "", quotation_number: "", currency: "MXN", tax_rate: 16, delivery_days: "",
  delivery_date: "", payment_terms: "", notes: "", lines: (req?.lines || []).map((l) => ({ item_number: l.item_number, description: l.description, qty: l.qty, unit_cost: l.est_cost || 0 })) });

export function QuotesPanel({ req, suppliers, canEdit, onSelected }) {
  const { t } = useI18n();
  const [cmp, setCmp] = useState(null);
  const [form, setForm] = useState(null);
  const [attachFor, setAttachFor] = useState(null);
  const [rfqOpen, setRfqOpen] = useState(false);
  const [hist, setHist] = useState(null);
  useEffect(() => {
    if (!form) { setHist(null); return; }
    api.get(`/requisitions/${req.id}/price-history`, { params: form.supplier_id ? { supplier_id: form.supplier_id } : {} })
      .then((r) => setHist(r.data)).catch(() => {});
  }, [form?.supplier_id, form ? req.id : null]); // eslint-disable-line react-hooks/exhaustive-deps
  const load = useCallback(() => api.get(`/requisitions/${req.id}/quotes/compare`).then((r) => setCmp(r.data)).catch(() => {}), [req.id]);
  useEffect(() => { load(); }, [load]);
  if (!cmp) return null;
  const quotes = cmp.quotes;
  const locked = req.status === "converted";

  const save = async () => {
    const payload = { ...form, tax_rate: Number(form.tax_rate), delivery_days: form.delivery_days === "" ? null : Number(form.delivery_days),
      lines: form.lines.map((l) => ({ item_number: l.item_number, qty: Number(l.qty), unit_cost: Number(l.unit_cost) })) };
    delete payload.id;
    try {
      if (form.id) await api.put(`/quotes/${form.id}`, payload); else await api.post(`/requisitions/${req.id}/quotes`, payload);
      toast.success(t("common.saved")); setForm(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const run = async (fn, msg) => { try { await fn(); toast.success(msg || t("common.saved")); load(); onSelected?.(); } catch (e) { toast.error(errMsg(e)); } };
  const onSupplier = (v) => { const s = suppliers.find((x) => x.id === v); setForm({ ...form, supplier_id: v, currency: s?.default_currency || form.currency, tax_rate: s?.tax_rate ?? form.tax_rate, payment_terms: s?.payment_terms || form.payment_terms }); };
  const ftotal = form ? form.lines.reduce((s, l) => s + Number(l.qty) * Number(l.unit_cost), 0) : 0;
  const lbl = "text-[10px] font-bold uppercase tracking-widest";

  return (
    <div className="border-t pt-3 space-y-3" data-testid="quotes-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className={`${lbl} text-muted-foreground flex items-center gap-1`}><Scale size={12} /> {t("qt.title")} ({quotes.length})</div>
        {canEdit && !locked && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="rounded-none text-xs h-7" onClick={() => setRfqOpen(true)} data-testid="rfq-open-btn">
              <Mail size={12} className="mr-1" /> {t("rfq.title")}{(req.rfq_log || []).length > 0 && ` (${req.rfq_log.length})`}
            </Button>
            <Button size="sm" variant="outline" className="rounded-none text-xs h-7" onClick={() => setForm(emptyQuote(req))} data-testid="quote-new-btn">
              <Plus size={12} className="mr-1" /> {t("qt.new")}
            </Button>
          </div>
        )}
      </div>
      {rfqOpen && <RfqDialog req={req} suppliers={suppliers} open={rfqOpen} onOpenChange={setRfqOpen} onSent={onSelected} />}
      {quotes.length === 0 ? <div className="text-xs text-muted-foreground italic" data-testid="quotes-empty">{t("qt.empty")}</div> : (
        <div className="tbl-wrap"><table className="tbl" data-testid="quotes-compare-table">
          <thead><tr>
            <th>{t("common.item")}</th><th className="num">{t("common.qty")}</th><th className="num">{t("rq.estCost")}</th>
            {quotes.map((q) => (
              <th key={q.id} className="num">
                <div className={q.status === "selected" ? "text-emerald-700" : ""}>{q.status === "selected" && <Trophy size={10} className="inline mr-1" />}{q.supplier_name}</div>
                <div className="text-[9px] font-normal normal-case text-muted-foreground">{q.quotation_number || "s/n"} · {q.currency}{q.delivery_days != null && ` · ${q.delivery_days} d`}</div>
              </th>
            ))}
          </tr></thead>
          <tbody>
            {cmp.items.map((it) => (
              <tr key={it.item_number}>
                <td><b>{it.item_number}</b> <span className="text-muted-foreground">{it.description}</span></td>
                <td className="num">{it.qty} {it.uom}</td><td className="num text-muted-foreground">{fmtMoney(it.est_cost)}</td>
                {quotes.map((q) => { const p = it.prices[q.id]; return (
                  <td key={q.id} className={`num ${p && p.unit_cost === it.best_unit_cost && quotes.length > 1 ? "text-emerald-700 font-bold bg-emerald-50" : ""}`}>
                    {p ? <>{fmtMoney(p.unit_cost)}<div className="text-[9px] text-muted-foreground font-normal">{fmtMoney(p.amount)}</div></> : <span className="text-muted-foreground">—</span>}
                  </td>); })}
              </tr>
            ))}
            <tr className="font-bold bg-muted/40">
              <td colSpan={3}>{t("common.total")} ({t("po.tax")} incl.)</td>
              {cmp.summary.map((s) => (
                <td key={s.quote_id} className={`num ${s.is_lowest_total && quotes.length > 1 ? "text-emerald-700" : ""}`} data-testid={`quote-total-${s.quote_id}`}>
                  {fmtMoney(s.total)} <span className="text-[9px]">{s.currency}</span>
                  <div className="text-[9px] font-normal text-muted-foreground">{s.best_price_items}/{s.total_items} {t("qt.bestPrices")}{s.is_fastest && quotes.length > 1 && ` · ${t("qt.fastest")}`}</div>
                </td>
              ))}
            </tr>
            <tr>
              <td colSpan={3}></td>
              {quotes.map((q) => (
                <td key={q.id} className="num">
                  <div className="flex flex-col gap-1 items-end">
                    {q.status === "selected" ? (
                      <span className="text-[10px] font-bold uppercase text-emerald-700 border border-emerald-300 bg-emerald-50 px-1.5 py-0.5" data-testid={`quote-selected-${q.id}`}>{t("qt.winner")}</span>
                    ) : canEdit && !locked && (
                      <Button size="sm" className="rounded-none text-[10px] h-6" onClick={() => run(() => api.post(`/quotes/${q.id}/select`), t("qt.selected"))} data-testid={`quote-select-${q.id}`}>
                        <Trophy size={10} className="mr-1" /> {t("qt.select")}
                      </Button>
                    )}
                    <div className="flex gap-1">
                      <button className="text-muted-foreground hover:text-primary" onClick={() => setAttachFor(q)} title={t("rq.attachments")} data-testid={`quote-attach-${q.id}`}><Paperclip size={12} /></button>
                      {canEdit && !locked && (
                        <>
                          <button className="text-muted-foreground hover:text-primary" onClick={() => setForm({ ...q, delivery_days: q.delivery_days ?? "" })} data-testid={`quote-edit-${q.id}`}><Pencil size={12} /></button>
                          {q.status === "selected" ? (
                            <button className="text-muted-foreground hover:text-destructive text-[10px]" onClick={() => run(() => api.post(`/quotes/${q.id}/unselect`))} data-testid={`quote-unselect-${q.id}`}>{t("qt.unselect")}</button>
                          ) : (
                            <button className="text-muted-foreground hover:text-destructive" onClick={() => { if (window.confirm(`${t("common.delete")}?`)) run(() => api.delete(`/quotes/${q.id}`)); }} data-testid={`quote-delete-${q.id}`}><Trash2 size={12} /></button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </td>
              ))}
            </tr>
          </tbody>
        </table></div>
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="rounded-none sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quote-dialog">
          {form && (
            <>
              <DialogHeader>
                <DialogTitle className="uppercase font-black">{form.id ? t("qt.edit") : t("qt.new")} · {req.number}</DialogTitle>
                <DialogDescription className="text-xs">{t("qt.hint")}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1 col-span-2"><Label className={lbl}>{t("common.supplier")}</Label>
                  <Select value={form.supplier_id} onValueChange={onSupplier}>
                    <SelectTrigger className="rounded-none h-8 text-xs" data-testid="quote-form-supplier"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1"><Label className={lbl}>{t("po.quotation")}</Label>
                  <Input value={form.quotation_number} onChange={(e) => setForm({ ...form, quotation_number: e.target.value })} className="rounded-none h-8 text-xs" data-testid="quote-form-number" /></div>
                <div className="space-y-1"><Label className={lbl}>{t("cur.code")}</Label>
                  <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} className="rounded-none h-8 text-xs" data-testid="quote-form-currency" /></div>
                <div className="space-y-1"><Label className={lbl}>{t("po.taxRate")}</Label>
                  <Select value={String(form.tax_rate)} onValueChange={(v) => setForm({ ...form, tax_rate: Number(v) })}>
                    <SelectTrigger className="rounded-none h-8 text-xs" data-testid="quote-form-tax"><SelectValue /></SelectTrigger>
                    <SelectContent>{[16, 8, 0].map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1"><Label className={lbl}>{t("qt.deliveryDays")}</Label>
                  <Input type="number" min="0" value={form.delivery_days} onChange={(e) => setForm({ ...form, delivery_days: e.target.value })} className="rounded-none h-8 text-xs" data-testid="quote-form-days" /></div>
                <div className="space-y-1"><Label className={lbl}>{t("po.deliveryDate")}</Label>
                  <Input type="date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} className="rounded-none h-8 text-xs" data-testid="quote-form-date" /></div>
                <div className="space-y-1 col-span-2"><Label className={lbl}>{t("po.paymentTerms")}</Label>
                  <Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} className="rounded-none h-8 text-xs" data-testid="quote-form-terms" /></div>
                <div className="space-y-1 col-span-3"><Label className={lbl}>{t("common.notes")}</Label>
                  <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none text-xs" data-testid="quote-form-notes" /></div>
              </div>
              <div className="space-y-1">
                <Label className={lbl}>{t("qt.prices")}</Label>
                {form.lines.map((l, i) => { const h = hist?.[l.item_number]; return (
                  <div key={l.item_number} className="flex items-center gap-2 text-xs">
                    <div className="flex-1 min-w-0">
                      <div className="truncate"><b>{l.item_number}</b> <span className="text-muted-foreground">{l.description}</span></div>
                      {h && h.count > 0 && (
                        <div className="text-[10px] text-muted-foreground flex items-center gap-1 flex-wrap" data-testid={`quote-price-hist-${i}`}>
                          <History size={9} />
                          <span>{t("ph.last")}: <b className="text-foreground">{fmtMoney(h.last.unit_cost)} {h.last.currency}</b> ({h.last.supplier_name}, {h.last.date})</span>
                          {h.last_supplier && <span>· {t("ph.thisSupplier")}: <b className="text-foreground">{fmtMoney(h.last_supplier.unit_cost)}</b> ({h.last_supplier.date})</span>}
                          <span>· {t("ph.min")}: <b className="text-emerald-700">{fmtMoney(h.min)}</b> · {t("ph.avg")}: {fmtMoney(h.avg)} · {h.count} OC</span>
                        </div>
                      )}
                    </div>
                    <Input type="number" value={l.qty} onChange={(e) => { const lines = [...form.lines]; lines[i] = { ...l, qty: e.target.value }; setForm({ ...form, lines }); }} className="rounded-none h-8 text-xs w-20" data-testid={`quote-line-qty-${i}`} />
                    <Input type="number" value={l.unit_cost} onChange={(e) => { const lines = [...form.lines]; lines[i] = { ...l, unit_cost: e.target.value }; setForm({ ...form, lines }); }}
                      className={`rounded-none h-8 text-xs w-28 ${h?.min != null && Number(l.unit_cost) > h.avg ? "border-amber-500" : ""}`} data-testid={`quote-line-cost-${i}`} />
                  </div>
                ); })}
                <div className="text-right text-xs">{t("po.subtotal")}: <b>{fmtMoney(ftotal)}</b> · {t("common.total")}: <b>{fmtMoney(ftotal * (1 + Number(form.tax_rate) / 100))} {form.currency}</b></div>
              </div>
              <Button onClick={save} disabled={!form.supplier_id} className="rounded-none font-bold" data-testid="quote-save-btn">{t("common.save")}</Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!attachFor} onOpenChange={(o) => !o && setAttachFor(null)}>
        <DialogContent className="rounded-none sm:max-w-xl">
          {attachFor && (
            <>
              <DialogHeader><DialogTitle className="uppercase font-black text-sm">{t("rq.attachments")} · {attachFor.supplier_name}</DialogTitle>
                <DialogDescription className="text-xs">{attachFor.quotation_number || "—"}</DialogDescription></DialogHeader>
              <EvidencePanel resourceType="quote" basePath={`/quotes/${attachFor.id}/evidence`} canWrite={canEdit && !locked} hideDelete={!canEdit} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
